<script setup lang="ts">
/**
 * Inline interactive building block for the `render_checkbox_group` tool: a
 * multi-choice question collected in-chat. Emits the array of selected option
 * ids; the dispatcher wraps it into the tool's result shape (`selectedOptionIds`).
 */
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface BlockOption {
  id: string;
  name: string;
  value?: number;
  description?: string;
}

const props = withDefaults(
  defineProps<{
    input: Record<string, unknown>;
    state: string;
    disabled?: boolean;
  }>(),
  { disabled: false },
);

const emit = defineEmits<{ (e: "submit", value: string[]): void }>();

const { t } = useI18n({ useScope: "local" });

const title = computed(() => (typeof props.input.title === "string" ? props.input.title : ""));
const options = computed<BlockOption[]>(() =>
  Array.isArray(props.input.options) ? (props.input.options as BlockOption[]) : [],
);

const resolved = computed(
  () =>
    props.disabled ||
    props.state === "output-available" ||
    props.state === "approval-responded" ||
    props.state === "output-error" ||
    props.state === "output-denied",
);

const selected = ref<string[]>([]);
const submitted = ref(false);
const readOnly = computed(() => resolved.value || submitted.value);

function toggle(optionId: string, checked: boolean | "indeterminate") {
  if (readOnly.value) {
    return;
  }
  const set = new Set(selected.value);
  if (checked === true) {
    set.add(optionId);
  } else {
    set.delete(optionId);
  }
  selected.value = [...set];
}

function submit() {
  if (readOnly.value || selected.value.length === 0) {
    return;
  }
  submitted.value = true;
  emit("submit", selected.value);
}

const fieldId = computed(() =>
  typeof props.input.id === "string" ? props.input.id : "checkbox-group",
);
</script>

<template>
  <div class="not-prose space-y-3 rounded-xl border border-border bg-card p-4" data-slot="chat-block-checkbox">
    <p v-if="title" class="text-sm font-medium text-foreground">{{ title }}</p>

    <div class="space-y-2" role="group" :aria-label="title || fieldId">
      <label
        v-for="option in options"
        :key="option.id"
        :class="[
          'flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors',
          selected.includes(option.id) ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
          readOnly ? 'cursor-default opacity-90' : '',
        ]"
      >
        <Checkbox
          :id="`${fieldId}-${option.id}`"
          :model-value="selected.includes(option.id)"
          :disabled="readOnly"
          class="mt-0.5"
          @update:model-value="(checked) => toggle(option.id, checked)"
        />
        <span class="flex min-w-0 flex-col">
          <Label :for="`${fieldId}-${option.id}`" class="cursor-[inherit] text-sm font-medium text-foreground">
            {{ option.name }}
          </Label>
          <span v-if="option.description" class="text-xs text-muted-foreground">
            {{ option.description }}
          </span>
        </span>
      </label>
    </div>

    <div v-if="!resolved" class="flex justify-end">
      <Button type="button" size="sm" :disabled="selected.length === 0 || submitted" @click="submit">
        {{ t("submit") }}
      </Button>
    </div>
  </div>
</template>

<i18n lang="yaml">
en:
  submit: Submit
</i18n>
